import React, { Suspense, useCallback, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { dataDogInit } from 'helpers/dataDog';

import {
  AccountManager,
  AnalyticsManager,
  ApiManager,
  Atlas,
  ExtensionsManager,
  getActiveForms,
  initDebug,
  IntlManager,
  isNotEmptyObject,
  Logger,
  NotificationManager,
  printMissingConfigError,
  SettingsManager,
  SiteConfigManager,
  TranslationProvider,
  useApplicationState,
  useSettings,
} from '@atlas/core';
import { onSignInSubmit, useActiveForm } from '@atlas/react-forms';

import FormWithCaptcha from 'common/Form/FormWithCaptcha/FormWithCaptcha';
import { CDN_TYPE_CMS, CDN_TYPE_SPORT, getCDNUrl, LocalConfig } from 'common/utils/utils';
import DebugInfo from 'src/components/DebugInfo/DebugInfo';

const ShareCss = React.lazy(() => import('modules/share-css'));

const SuspenseCssComponent = () => (
  <Suspense fallback={<span style={{ display: 'none' }} />}>
    <ShareCss />
  </Suspense>
);

dataDogInit();
initDebug();

// Only for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line
  const localCmsSettings = require('./cms.settings.js');
  SiteConfigManager.setConfig(localCmsSettings.default);
}

// Only for production
if (process.env.NODE_ENV === 'production') {
  // Applying default client application settings
  if (isNotEmptyObject(window.CMS_CONFIG)) {
    const { navigation = {}, page = {} } = window.CMS_CONFIG;

    // FIXME
    SiteConfigManager.setConfig({ ...page, navigation });
  }
}

// Initializing api default headers
const defaultHeaders = {
  device: 'desktop',
};

const projectId = LocalConfig.get('projectId');

// Providing project id if it exist in config
if (projectId) {
  defaultHeaders['x-project-id'] = projectId;
}

// Providing header for local testing
if (process.env.NODE_ENV === 'development') {
  defaultHeaders['x-project-team'] = 'frontend-team';
}

SiteConfigManager.device = 'desktop';
// Setting application version & device type
ApiManager.setDefaultHeaders(defaultHeaders);

// Resolving stored settings
SettingsManager.resolveSettings();

const { host, protocol } = window.location;

ApiManager.setHostname('core', `${protocol}//${host}/api/`);
ApiManager.setHostname('cms', `${protocol}//${host}/api/cms-go/v1/site/`);
ApiManager.setHostname('cms/v2', `${protocol}//${host}/api/cms-go/v2/site/`);
ApiManager.setHostname('cdn', getCDNUrl(CDN_TYPE_SPORT));

AnalyticsManager.init({});

// Creating atlas extensions manager
window.aem = new ExtensionsManager();

const HOSTS_MAP = {
  'm.1pra1.bet': 'm.1pra1.bet.br',
  'm.geralbet.com': 'm.geralbet.bet.br',
  'm.luck.bet': 'm.luck.bet.br',
  'm.luva.bet': 'm.luva.bet.br',
  'm.mmabet.com': 'm.mma.bet.br',
  'm.onabet.com': 'm.onabet.bet.br',
  'm.onabet15.com': 'm.onabet.bet.br',
  // mirrors
  'm.onabet4.com': 'm.onabet.bet.br',
  'm.realsbet.com': 'm.realsbet.com',
  'm.startbet.io': 'm.start.bet.br',
  'm.zedocash.com': 'm.zedocash.bet.br',
  'realsbet.m.preprod.k8s-hz.atlas-iac.com': 'lucksports.m.preprod.k8s-hz.atlas-iac.com',
  // just for testing purposes
  'realsbet.m.test.k8s-hz.atlas-iac.com': 'lucksports.m.test.k8s-hz.atlas-iac.com',
};

const LogoImage = ({ logoSrc, metaTitle }) => {
  if (!logoSrc) return null;

  return <img alt={metaTitle} className="logo__img" src={logoSrc} />;
};

const HeaderInfoAuthorizedBalance = () => {
  const { wallet: { balance } = {} } = useApplicationState();
  const decimalBalance = IntlManager.formatMoney(balance);

  return <span className="title-small-bold-text">{decimalBalance}</span>;
};

const Header = ({ signUpButton }) => {
  const { theme } = useSettings();
  const { authenticated } = useApplicationState();

  const metaTitle = SiteConfigManager.getConfig('config.config.seo.default_meta_title', '');

  const logoImgSrc = useMemo(() => {
    const cdnPathName = getCDNUrl(CDN_TYPE_CMS);
    try {
      const logoName = `logo_desktop_${theme || SYS_CONFIG.defaultTheme || 'dark'}`;
      const logo = SiteConfigManager.getConfig(logoName);

      if (!logo) {
        printMissingConfigError(logoName);
      }

      return `${cdnPathName}/${logo}`;
    } catch (error) {
      Logger.error('unable to load logo', error);

      return '';
    }
  }, [theme]);

  return (
    <div className="header header-redirect">
      <LogoImage logoSrc={logoImgSrc} metaTitle={metaTitle} />
      {signUpButton}
      {authenticated && (
        <div className="header-content">
          <HeaderInfoAuthorizedBalance />
        </div>
      )}
    </div>
  );
};

const Form = () => {
  const { activeStep, activeType, submit } = useActiveForm(
    'CLIENT_LOGIN',
    onSignInSubmit(AccountManager.loginWithForm.bind(AccountManager)),
    getActiveForms,
  );

  const submitHandler = useCallback(data => {
    if (data?.is2FaEnabled) {
      NotificationManager.display({
        data,
        type: 'twoFactorLogin',
      });
    }
  }, []);

  const renderForm = activeType ? (
    <FormWithCaptcha
      action={submit}
      buttonClassName="block"
      buttonId="signIn"
      buttonText="sign_in"
      className="form-redirect"
      form={{ fields: activeType?.steps[0]?.fields, number: activeStep }}
      formType={activeType}
      id={activeType.id}
      onSubmit={submitHandler}
    />
  ) : null;

  return renderForm;
};

const RedirectView = () => {
  const { authenticated, loading, resolved } = useApplicationState();
  const moving = HOSTS_MAP[location.hostname || location.host];

  const newDomain = useMemo(() => {
    return new URL(location.protocol.concat('//', moving)).toString();
  }, [moving]);

  const areButtonsVisible = !!(authenticated && moving);
  const isFormVisible = !!(!authenticated && moving);
  const signUpButton = !!isFormVisible && (
    <div className="header-content">
      <a className="btn medium primary" href={newDomain}>
        Registre-se
      </a>
    </div>
  );

  if (loading && !resolved) return <div className="redirect-loader" />;

  return (
    <div className="page redirect-page">
      <Header signUpButton={signUpButton} />
      <div className="redirect-container">
        <div className="redirect-panel medium">
          <div className="content sub-title-text redirect-message redirect-message">
            {moving ? (
              <>
                <p>Prezado(a) usuário(a),</p>
                <p>
                  Como parte de nossos preparativos para operarmos em ambiente regulamentado, o que
                  ocorrerá em 1º de janeiro de 2025, queremos garantir que sua experiência seja a
                  mais tranquila possível. Por isso estamos atualizando nossos Termos e Condições de
                  Uso.
                </p>
                <p>
                  Ao clicar em “Concordar”, você aceitará expressamente essas atualizações, no que
                  diz respeito à transferência de seus dados, recursos e eventuais apostas em aberto
                  que passarão a ser mantidos e custodiados na nossa nova plataforma em 1º de
                  janeiro de 2025; e também aceitará o armazenamento de seus dados em uma base
                  localizada fora do Brasil.
                </p>
                <p>
                  Case não concorde com a transferência ou armazenamento, o seu acesso aos serviços
                  desta Plataforma continuará sendo permitido até o dia 31/12/2024. Após essa data
                  será possível, apenas, resgatar os recursos depositados em sua conta. Quantos às
                  apostas em aberto, estas poderão ser canceladas, com a restituição integral do
                  valor apostado via uma de suas contas cadastradas na plataforma.
                </p>
                <p>
                  Para obter mais informações, acesse os Termos e Condições de Uso{' '}
                  <a className="redirect-link" href={newDomain.concat('page/terms-and-conditions')}>
                    Política de Privacidade
                  </a>
                  .
                </p>
              </>
            ) : (
              <>
                <p>
                  Estamos atualizando nossos sistemas para ligar novos serviços para o mercado
                  regulamentado do Brasil. Em breve você vai poder jogar com a gente com novas
                  regras de segurança e responsabilidade.
                </p>
                <p>Fique ligado!</p>
              </>
            )}
          </div>

          {areButtonsVisible && (
            <div className="redirect-view-buttons">
              <a className="btn big primary redirect-button" href={newDomain}>
                Consentimento
              </a>
              <a className="btn big secondary redirect-button" href={newDomain}>
                Não consentimento
              </a>
            </div>
          )}

          {isFormVisible && <Form />}
        </div>
      </div>
    </div>
  );
};

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <>
    <SuspenseCssComponent />
    <Atlas>
      <TranslationProvider>
        <RedirectView />
        <DebugInfo />
      </TranslationProvider>
    </Atlas>
  </>,
);
